import { useEffect, useState } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { toast } from 'sonner'
import { Download, X, Share, Plus } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'solim_pwa_install_dismissed'

const isIos = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent)
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  // @ts-expect-error iOS Safari propriétaire
  window.navigator.standalone === true

const PWAPrompts: React.FC = () => {
  const [installEvt, setInstallEvt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showCard, setShowCard] = useState(false)
  const [iosHint, setIosHint] = useState(false)

  // ── Mise à jour du service worker ──
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, r) {
      if (r) setInterval(() => r.update(), 60 * 60 * 1000) // re-check toutes les heures
    },
  })

  useEffect(() => {
    if (needRefresh) {
      toast('Nouvelle version disponible', {
        duration: Infinity,
        action: { label: 'Recharger', onClick: () => updateServiceWorker(true) },
      })
    }
  }, [needRefresh, updateServiceWorker])

  // ── Détection installabilité ──
  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return

    // Android / Chrome / Edge : event natif
    const onPrompt = (e: Event) => {
      e.preventDefault()
      setInstallEvt(e as BeforeInstallPromptEvent)
      setShowCard(true)
    }
    window.addEventListener('beforeinstallprompt', onPrompt)

    // iOS Safari : pas d'event → afficher les instructions manuelles
    if (isIos()) {
      setIosHint(true)
      setShowCard(true)
    }

    return () => window.removeEventListener('beforeinstallprompt', onPrompt)
  }, [])

  const handleInstall = async () => {
    if (!installEvt) return
    await installEvt.prompt()
    await installEvt.userChoice
    setInstallEvt(null)
    setShowCard(false)
  }

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1')
    setShowCard(false)
  }

  if (!showCard) return null

  return (
    <div
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 88,
        zIndex: 1000,
        maxWidth: 448,
        margin: '0 auto',
        background: 'linear-gradient(135deg, var(--sl-accent) 0%, var(--sl-accent-bright) 100%)',
        borderRadius: 20,
        padding: '14px 16px',
        display: 'flex',
        alignItems: iosHint ? 'flex-start' : 'center',
        gap: 12,
        boxShadow: 'var(--sl-shadow-btn)',
        fontFamily: "'Jost', sans-serif",
      }}
    >
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: 12,
          flexShrink: 0,
          background: 'rgba(255,255,255,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Download style={{ width: 21, height: 21, color: '#fff' }} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            fontFamily: "'Bodoni Moda', serif",
            fontStyle: 'italic',
            fontWeight: 700,
            fontSize: 15,
            color: '#fff',
            margin: 0,
          }}
        >
          Installer Solim
        </p>
        {iosHint ? (
          <p
            style={{
              fontSize: 12,
              color: 'rgba(255,255,255,0.9)',
              margin: '3px 0 0',
              lineHeight: 1.45,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 4,
            }}
          >
            Appuyez sur
            <Share style={{ width: 14, height: 14, display: 'inline', verticalAlign: 'middle' }} />
            puis « Sur l'écran d'accueil »
            <Plus style={{ width: 14, height: 14, display: 'inline', verticalAlign: 'middle' }} />
          </p>
        ) : (
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', margin: 0, lineHeight: 1.35 }}>
            Accès rapide depuis votre écran d'accueil
          </p>
        )}
      </div>

      {!iosHint && (
        <button
          onClick={handleInstall}
          style={{
            background: '#fff',
            color: 'var(--sl-accent)',
            border: 'none',
            cursor: 'pointer',
            borderRadius: 12,
            padding: '9px 14px',
            fontWeight: 700,
            fontSize: 13,
            fontFamily: "'Jost', sans-serif",
            flexShrink: 0,
          }}
        >
          Installer
        </button>
      )}

      <button
        onClick={dismiss}
        aria-label="Fermer"
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, flexShrink: 0 }}
      >
        <X style={{ width: 18, height: 18, color: 'rgba(255,255,255,0.8)' }} />
      </button>
    </div>
  )
}

export default PWAPrompts
